import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import {
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { reportsApi } from '../services/api';

interface EmailFormData {
  email: string;
  reportType: string;
  frequency: string;
}

const Reports: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingSample, setIsSendingSample] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset, getValues } = useForm<EmailFormData>();

  const onSubmit = async (data: EmailFormData) => {
    setIsSubmitting(true);
    try {
      await reportsApi.sendDailyReport(data.email);
      enqueueSnackbar('Report scheduled successfully!', { variant: 'success' });
      reset();
    } catch (error) {
      enqueueSnackbar('Failed to schedule report', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendSampleReport = async () => {
    const emailValue = getValues('email');
    
    if (!emailValue) {
      enqueueSnackbar('Please enter an email address first', { variant: 'warning' });
      return;
    }

    setIsSendingSample(true);
    try {
      const response = await reportsApi.sendTestEmail(emailValue);
      
      // Check if email service is not configured
      if (response.data?.note) {
        enqueueSnackbar('Email service needs configuration. Opening preview instead...', { variant: 'info' });
        // Open the HTML report in a new tab as a preview
        window.open('/api/reports/generate-html', '_blank');
      } else {
        enqueueSnackbar(`Sample report sent to ${emailValue}`, { variant: 'success' });
      }
    } catch (error: any) {
      if (error.response?.data?.note) {
        enqueueSnackbar('SMTP not configured. Contact IT to whitelist server IP in Google Workspace.', { variant: 'warning' });
        // Open the HTML report as fallback
        window.open('/api/reports/generate-html', '_blank');
      } else {
        enqueueSnackbar('Failed to send sample report', { variant: 'error' });
      }
    } finally {
      setIsSendingSample(false);
    }
  };

  const reportFeatures = [
    'Daily sales vs. same day last year',
    'Month-to-date vs. MTD last year',
    'Year-to-date vs. YTD last year',
    'Trailing 12-month performance',
    'Store-by-store breakdown',
    'Peak hours analysis',
    'Category performance metrics',
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        Automated Reports
      </Typography>

      <Grid container spacing={4}>
        {/* Report Configuration */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Configure Email Reports
            </Typography>
            
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Report Type</InputLabel>
                    <Select
                      defaultValue="daily"
                      label="Report Type"
                      {...register('reportType')}
                    >
                      <MenuItem value="daily">Daily Summary</MenuItem>
                      <MenuItem value="weekly">Weekly Summary</MenuItem>
                      <MenuItem value="monthly">Monthly Summary</MenuItem>
                      <MenuItem value="custom">Custom Report</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      defaultValue="daily"
                      label="Frequency"
                      {...register('frequency')}
                    >
                      <MenuItem value="daily">Every Day</MenuItem>
                      <MenuItem value="weekdays">Weekdays Only</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Reports are sent at 6:00 AM local time
                  </Alert>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={isSubmitting}
                    startIcon={<EmailIcon />}
                  >
                    {isSubmitting ? 'Scheduling...' : 'Schedule Report'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>

          {/* Active Subscriptions */}
          <Paper sx={{ p: 4, mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Active Report Subscriptions
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="Daily Sales Report"
                  secondary="nick.savaya@savvysliders.com - Every day at 6:00 AM"
                />
                <Button size="small" color="error">
                  Cancel
                </Button>
              </ListItem>
              <Divider component="li" />
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="Weekly Performance Report"
                  secondary="manager@savvysliders.com - Every Monday at 8:00 AM"
                />
                <Button size="small" color="error">
                  Cancel
                </Button>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Report Information */}
        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  Report Contents
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Each automated report includes comprehensive sales data and performance metrics:
              </Typography>
              <List dense>
                {reportFeatures.map((feature, index) => (
                  <ListItem key={index}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircleIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  Sample Report Preview
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Want to see what your report will look like?
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleSendSampleReport}
                disabled={isSendingSample}
                startIcon={<EmailIcon />}
              >
                {isSendingSample ? 'Sending...' : 'Send Sample Report'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Reports;